import { Platform } from '@angular/cdk/platform';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonToggle } from '@angular/material/button-toggle';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AbcCore, CollectionHelper, CompareHelper, DotNetBridge, ProfileSettingsViewModel } from '@barthauer/abc/core';
import { AssetsManagerService } from '@barthauer/abc/core/assets-manager';
import { ProfileInfo, ProfileService } from '@barthauer/abc/core/authentication';
import { CacheStorageAdapter, DataService, ObjectSpaceService } from '@barthauer/abc/core/data';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { AbcContainerService, AbcWindowComponent, IWindow, WindowStyle } from '@barthauer/abc/ui-components';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';

import { ParameterService } from 'libs/abc/queries/parameters/values-editor/src/lib/services/parameter.service';
import { interval, of, Subscription } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { DataEntity, FeatureGeometryType, GeoLocationPosition, MapActionIds, MapLayerType } from '..';
import { ContextMenuController } from './context-menu-controller';
import { FcDirective } from './controls/fc-host.directive';
import { MapScaletextComponent } from './controls/map-scaletext/map-scaletext.component';
import { MapTooltipComponent } from './controls/map-tooltip/map-tooltip.component';
import { MeasureComponent } from './controls/measure/measure.component';
import { MovenodeControllerComponent } from './controls/movenode-controller/movenode-controller.component';
import { NetworktracingComponent } from './controls/networktracing/networktracing.component';
import { PrintComponent } from './controls/print/print.component';
import { MapLoader } from './map.loader';
import { MapSelection } from './map.selection';
import { MapView } from './map.view';
import { ContextMenuConfiguration } from './models/contextmenu.model';
import { LegendSettings } from './models/legend-settings.model';
import { MapConfiguration } from './models/map.config';
import { LegendSettingsService } from './services/legend-settings.service';
import { MapService } from './services/map.service';
import { FeatureSelectionMode, FeatureSelectorComponent } from './controls/feature-selector/feature-selector.component';
import { AbcConfigService } from '@barthauer/abc/core/configuration';

declare let ol: any;
declare let turf: any;

export enum ActionType {
  Default,
  ZoomWin,
  InsertPoint,
  InsertLine,
  InsertPolygon,
  InsertTicket,
  Move,
  MeasureLine,
  MeasureArea,
  MeasureCoord,
  PrintMap,
  Pan,
  SelectEntities,
}

export const LAYER_PRINT_BOX: string = 'layer_print_box';

@Component({
  selector: 'map-component',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class MapComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('progressSpinner', { static: true })
  progressSpinner: MatProgressSpinner;
  @ViewChild('mapElement', { static: true }) mapElement: ElementRef;
  @ViewChild('mapTooltip', { static: true })
  mapTooltip: MapTooltipComponent;
  @ViewChild('mapScaletext', { static: true })
  mapScaletext: MapScaletextComponent;
  @ViewChild(FcDirective) fcHost: FcDirective;
  @ViewChild('toolMeasureLine') toolMeasureLine: MatButtonToggle;
  @ViewChild('toolMeasureArea') toolMeasureArea: MatButtonToggle;
  @ViewChild('toolMeasureCoord') toolMeasureCoord: MatButtonToggle;
  @ViewChild('toolAddPoint') toolAddPoint: MatButtonToggle;
  @ViewChild('toolAddTicket') toolAddTicket: MatButtonToggle;
  @ViewChild('toolAddLine') toolAddLine: MatButtonToggle;
  @ViewChild('toolAddPolygon') toolAddPolygon: MatButtonToggle;
  @ViewChild('toolZoomArea') toolZoomArea: MatButtonToggle;
  @ViewChild('toolPan') toolPan: MatButtonToggle;
  @ViewChild('toolSelectEntities') toolSelectEntities: MatButtonToggle;

  @Input('rtlEnabled') rtlEnabled: boolean;
  get rtl(): boolean {
    return this.rtlEnabled;
  }
  @ViewChild('abcWindowContainer', { read: ViewContainerRef })
  abcWindow: ViewContainerRef;

  ActionType = ActionType;

  private isMeasureMenuOpen_ = false;
  private isConstructionMenuOpen_ = false;
  private isExtentMenuOpen_ = false;
  public action: ActionType = ActionType.Default;

  public readonly mapid = 'map';
  public readonly overviewid = 'overviewmap';
  public readonly attributionid = 'attribution';
  public readonly mapscaleid = 'mapscalebutton';
  public readonly rotateid = 'rotatebutton';

  private readonly ReadMapUrl = 'Maps/ReadMap';
  private readonly ReadMapImageUrl = 'Maps/ReadMapImage';
  private readonly TransformMapValueUrl = 'Maps/TransformMapValue';
  private readonly PrepareObjectGraphics = 'Maps/PrepareObjectGraphics';
  private readonly NEW_TICKET_ACTION = 'Opera_NewTicket';

  private currentMapConfiguration: MapConfiguration;

  isMapGeoLocationActive: boolean = false;
  isMapLoaded: boolean = false;
  isMobileEnvironment: boolean = false;
  selectionEntitiesAction: (selection: DataEntity[]) => void;
  selectEntitiesDefaultAction: (selection: DataEntity[]) => void;

  private geoLocationPointFeature: any;
  private geoLocationPointLayer: any;
  private subscriptions: Subscription[] = [];
  private openWindowInstances: AbcWindowComponent[] = [];
  private mapLoader: MapLoader;
  private mapView: MapView;
  private mapRotationEnabled: boolean;
  private mapSelection: MapSelection;
  private olmap: any;

  private draw = undefined;
  private snap = undefined;
  private dragPan = undefined;
  private vectorSource: any = undefined;
  private vectorEditlayer: any = undefined;

  click: boolean = false;
  private delay: number = 350; // OpenLayers render delay = 350
  enablePrevButton: boolean = false;
  enableNextButton: boolean = false;

  private contextMenuController: ContextMenuController;
  private measureComponent: MeasureComponent;
  private profileInfo: ProfileInfo;
  private networkTracingDialog: NetworktracingComponent;
  windowInstance: AbcWindowComponent;

  private moveNodeController: MovenodeControllerComponent = undefined;
  private featureSelector: FeatureSelectorComponent = undefined;

  get hasTargetUrlCopy(): boolean {
    return (!!this.mapLoader?.definition?.target?.name && !this.isMobileEnvironment) || this.hasTargetUrlOpen;
  }
  get hasTargetUrlOpen(): boolean {
    return !!this.mapLoader?.definition?.target?.url;
  }
  hasEditGeometryPoint: boolean = false;
  hasEditGeometryLine: boolean = false;
  hasEditGeometryPolygon: boolean = false;
  hasActionNewTicket: boolean = false;

  isFirstConstructionAction: boolean = false;
  isMapPrintCommandAvailable: boolean = undefined;
  public res = ResourceSet.Empty;
  public selectedFeatures: DataEntity[] = [];
  private PRINT_DIALOG_ID: string = 'printDialog';

  private initialCommandInfo: { name: string; entities: DataEntity[] };

  constructor(
    private abcContainer: AbcContainerService,
    private assetsService: AssetsManagerService,
    private resourceService: ResourceManagerService,
    private mapService: MapService,
    platform: Platform,
    private renderer: Renderer2,
    private coreDataService: DataService,
    private objectSpaceService: ObjectSpaceService,
    private abcCore: AbcCore,
    private componentResolver: ComponentFactoryResolver,
    private changeDetectorRef: ChangeDetectorRef,
    private profileService: ProfileService,
    private legendSettingsService: LegendSettingsService,
    private parameterService: ParameterService,
    private configService: AbcConfigService,
  ) {
    // this.isMobileEnvironment =
    //   platform.ANDROID || platform.IOS || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    this.isMobileEnvironment = this.isCordovaApp();
    this.mapService.onIsMobileEnvironment().subscribe((isMobile) => {
      this.isMobileEnvironment = isMobile;
      this.changeDetectorRef.detectChanges();
    });

    mapService.executeCommand = async (name, entities) => {
      this.initialCommandInfo = { name: name, entities: entities };
    };

    this.subscriptions.push(
      this.mapService
        .onMapConfigReceived()
        .pipe(
          concatMap((cfg) => {
            return this.legendSettingsService.getCurrentSettings().pipe(
              map((s) => {
                if (s?.viewbox?.length === 4) {
                  cfg.InitialExtent = s.viewbox;
                }
                return cfg;
              }),
            );
          }),
        )
        .subscribe(
          async (mapConfig) => {
            await this.loadMapByConfiguration(mapConfig);
          },
          (error) => {
            this.abcCore.error.publish(error);
            console.log(error);
          },
        ),
    );

    this.mapService.supportsCreateGeometry = (points, lines, polygons) => {
      this.hasEditGeometryPoint = points;
      this.hasEditGeometryLine = lines;
      this.hasEditGeometryPolygon = polygons;
    };

    this.mapService.prepareObjectGraphics = (entities) => {
      let body = {
        ObjectKeys: entities.map((_) => {
          return { Id: _.id, ObjectType: _.objectType };
        }),
        Parameters: this.mapService.getCurrentQueryParameters(),
      };

      return this.abcCore.data
        .executeWebApiPOSTMethod(
          this.getWebApiUrl(this.currentMapConfiguration.WebApiUrl, this.PrepareObjectGraphics),
          body,
        )
        .pipe(map((_) => {}));
    };

    this.mapService.getCurrentView = () => this.mapView;

    this.mapService.moveToNewCoordinate = async (coordinate) => await this.moveToNewCoordinate(coordinate);
    this.mapService.startInteractiveGeometryCapture = async (featureGeometryType) =>
      await this.drawFeatureGeometry(featureGeometryType);
    this.mapService.enableContextMenu = (enable: boolean) => this.contextMenuController?.setActive(enable);
    this.mapService.selectDataEntities = async () => await this.selectDataEntitiesInteractively();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.onMeasureValChange(ActionType.Default);
    }
  }

  private isCordovaApp(): boolean {
    return (
      !!(<any>window).cordova ||
      (document.URL.indexOf('http://') === -1 && document.URL.indexOf('https://') === -1) ||
      typeof window['device'] !== 'undefined'
    );
  }

  cancelDrawing() {
    if (this.draw) {
      this.olmap.removeInteraction(this.draw);
      this.clearEditInteractions();
    }
  }

  toggleMeasureMenu() {
    this.closeConstructionMenu();
    this.closeExtentMenu();
    this.isMeasureMenuOpen_ = !this.isMeasureMenuOpen_;
  }

  closeMeasureMenu() {
    this.isMeasureMenuOpen_ = false;
  }

  isMeasureMenuOpen(): boolean {
    return this.isMeasureMenuOpen_;
  }

  toggleConstructionMenu(): void {
    this.closeMeasureMenu();
    this.closeExtentMenu();
    this.isConstructionMenuOpen_ = !this.isConstructionMenuOpen_;
  }

  closeConstructionMenu(): void {
    this.isConstructionMenuOpen_ = false;
  }

  isConstructionMenuOpen(): boolean {
    return this.isConstructionMenuOpen_;
  }

  toggleExtentMenu(): void {
    this.closeMeasureMenu();
    this.closeConstructionMenu();
    this.isExtentMenuOpen_ = !this.isExtentMenuOpen_;
  }

  closeExtentMenu(): void {
    this.isExtentMenuOpen_ = false;
  }

  isExtentMenuOpen(): boolean {
    return this.isExtentMenuOpen_;
  }

  isConstructionMenuDisabled(): boolean {
    return !this.isMapLoaded || CacheStorageAdapter.isOffline;
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('abc-graphics-adapters-open-layers');
  }

  async ngOnDestroy() {
    await this.saveViewbox(null);
    console.log('map_destroy');
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
    this.openWindowInstances.forEach((element) => {
      element.closeDialog();
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  @HostListener('window:visibilitychange', ['$event'])
  @HostListener('document:pause', ['$event'])
  async saveViewbox($event: Event): Promise<void> {
    const e = $event;
    if (e?.type === 'visibilitychange' && document?.visibilityState === 'visible') {
      return;
    }
    await this.legendSettingsService
      .getCurrentSettings()
      .pipe(
        concatMap((settings) => {
          if (!settings) {
            settings = new LegendSettings();
          }
          settings.viewbox = this.mapView?.getCurrentExtentWithDataProjection() ?? settings.viewbox;
          return this.legendSettingsService.setCurrentSettings(settings);
        }),
      )
      .toPromise();
  }

  ngAfterViewInit() {
    this.assetsService
      .loadDependencies([
        'assets/lib-open-layers/map/ol.css',
        'assets/lib-open-layers/map/ol.js',
        'assets/lib-open-layers/map/proj4.js',
        'assets/lib-open-layers/map/turf.js',
        'assets/lib-open-layers/map/geotiff.js',
      ])
      .then((data) => {
        this.subscriptions.push(
          this.mapService.onGeoLocationStateChanged().subscribe(
            (state) => {
              this.isMapGeoLocationActive = state.trackingActive;
              if (!this.isMapGeoLocationActive) {
                this.removeGeoLocationPointFeature();
              }
            },
            (error) => {
              console.log(error);
            },
          ),
        );
        this.subscriptions.push(
          this.mapService.onGeoLocationPositionChanged().subscribe(
            (position) => {
              if (this.drawGeoLocationPoint(position, '#3399CC')) {
                this.isMapGeoLocationActive = true;
              }
            },
            (error) => {
              if (error.code !== 3) {
                //avoiding 3 = time out error, which pops up sometimes on ios. It's useless but annoying
                this.abcCore.error.publish(error);
              }
              console.log(error);
            },
          ),
        );
        this.mapService.raiseMapInitiated();

        this.subscriptions.push(
          this.mapService.onObjectNavigation().subscribe(
            (objectInfos) => {
              this.mapSelection?.moveToObjects(objectInfos);
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );
        this.subscriptions.push(
          this.mapService.onDataRowNavigation().subscribe(
            (rows) => {
              this.mapSelection?.moveToDataRows(rows);
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );
        this.subscriptions.push(
          this.mapService.onObjectNavigationWithoutZoom().subscribe(
            (objectInfos) => {
              this.mapSelection?.moveToObjectsWithoutZoom(objectInfos);
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.onNetworkTracing().subscribe(
            (url) => {
              this.startNetworkTracing(url);
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.onRotationEnabled().subscribe(
            (enabled) => {
              this.mapRotationEnabled = enabled;
              this.mapView?.setRotationEnabled(enabled);
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.onRefreshLayerRequested().subscribe(
            (objectInfos) => {
              this.mapSelection.refreshLayerSources(objectInfos);
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.onSetCustomColor().subscribe(
            (data) => {
              this.mapLoader.setCustomColor(data?.entities, data?.color);
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.mapZoomToLayer().subscribe(
            (node) => {
              this.mapSelection.zoomToLayer(node.layer);
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.onStartExtentSelection().subscribe(
            () => {
              this.mapView?.beginDragExtent();
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.onMapSelectionActionChanged().subscribe(
            (action) => {
              this.selectionEntitiesAction = action;
              this.changeDetectorRef.detectChanges();
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.onMapSelectEntitiesDefaultActionChanged().subscribe(
            (action) => {
              this.selectEntitiesDefaultAction = action;
              this.changeDetectorRef.detectChanges();
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );

        this.subscriptions.push(
          this.mapService.onMapAction().subscribe(
            (arg) => {
              switch (arg.actionId) {
                case MapActionIds.MARK_COORDINATE:
                  this.addMarker(arg.payload.latOrX, arg.payload.lonOrY, arg.payload.isProjected);
                  break;
                case MapActionIds.CLEAR_MARKERS:
                  this.clearMarkers();
                  break;
                case MapActionIds.CLEAR_SELECTIONS:
                  this.clearFeatureSelections();
                  break;
              }
            },
            (error) => {
              this.abcCore.error.publish(error);
              console.log(error);
            },
          ),
        );
      })
      .catch((error) => console.log(error));

    const handler = async () => {
      if (CacheStorageAdapter.isOffline || CacheStorageAdapter.forceOfflineMode) {
        if (
          this.action === ActionType.InsertPoint ||
          this.action === ActionType.InsertLine ||
          this.action === ActionType.InsertPolygon
        ) {
          this.action = ActionType.Default;
          this.clearEditInteractions();
          this.updateContextMenuState();
        }
      }
    };

    CacheStorageAdapter.isOfflineChanged.subscribe(async () => await handler());
  }

  notifyProfileInfo(profileInfo: ProfileInfo): void {
    this.profileInfo = profileInfo;
  }

  private addMarker(latOrX: number, lonOrY: number, isProjected: boolean = false) {
    this.clearEditInteractions();
    this.ensureEditSourceForMarker();

    let projected: number[];

    let point: any;
    this.vectorSource.clear();
    const mapProjection = this.olmap.getView().getProjection();
    const worldProjection = ol.proj.get('EPSG:4326');
    if (isProjected) {
      projected = [latOrX, lonOrY];
      point = new ol.geom.Point(projected);

      const transformed_pt = this.mapView.transformGeometryToFeatureProjection(point);
      var lonlat = transformed_pt?.clone()?.transform(mapProjection, worldProjection);
      if (this.isValidPoint(lonlat)) {
        point = transformed_pt;
      } else {
        this.mapService.sendMapShortMessage(this.res.get('Map-Coordinate-OutofRange'));
        return;
      }
    } else {
      projected = ol.proj.transform([lonOrY, latOrX], worldProjection, mapProjection);
      point = new ol.geom.Point(projected);
    }

    const feature = new ol.Feature({ geometry: point });

    const iconStyle = new ol.style.Icon({
      src: 'assets/resources/abc-graphics-adapters-open-layers/map/location-marker.svg',
      anchor: [0.5, 1],
      scale: 2.0,
      crossOrigin: 'anonymous',
    });

    const style = new ol.style.Style({
      image: iconStyle,
    });

    feature.setStyle(style);

    this.vectorSource.addFeature(feature);
    this.vectorSource.changed();
    this.changeDetectorRef.detectChanges();
    this.mapView?.setCenter(point.getCoordinates());
  }

  private isValidPoint(pt: any): boolean {
    let retValid: boolean = false;
    try {
      if (!!pt) {
        const [x, y] = pt.getCoordinates();
        retValid = isFinite(x) && isFinite(y);
      }
    } catch (err) {
      console.log(err);
    }

    return retValid;
  }
  private clearMarkers() {
    if (this.vectorSource) {
      this.vectorSource.clear();
      this.vectorSource.changed();
      this.changeDetectorRef.detectChanges();
    }
  }

  private clearFeatureSelections() {
    this.mapSelection?.clearSelectedFeatures();
    this.changeDetectorRef.detectChanges();
  }

  private async loadMapByConfiguration(mapConfig: MapConfiguration): Promise<void> {
    if (mapConfig) {
      this.removeMap();
      this.isMapLoaded = false;

      this.currentMapConfiguration = mapConfig;
      await this.initMap();
    }
  }

  private removeMap() {
    const childElements = this.mapElement.nativeElement.childNodes;
    for (let child of childElements) {
      this.renderer.removeChild(this.mapElement.nativeElement, child);
    }
  }

  viewZoomIn(): void {
    this.mapView?.zoomIn();
  }

  viewZoomOut(): void {
    this.mapView?.zoomOut();
  }

  viewInitial(): void {
    this.closeExtentMenu();
    this.mapView?.zoomInitial();
  }

  copyTargetUrl(): void {
    this.closeExtentMenu();
    const url = '';
    try {
      const selBox = document.createElement('textarea');
      const params = new URLSearchParams();
      params.append('x', this.mapView.getCurrentCenterWithDataProjection()[0].toString());
      params.append('y', this.mapView.getCurrentCenterWithDataProjection()[1].toString());
      params.append('scale', this.mapView.getCurrentScale().toString());
      params.append('rotation', ((this.mapView.getRotation() / Math.PI) * 180).toString());
      selBox.value = document.location.href?.split('?')[0] + '?' + params.toString();
      document.body.appendChild(selBox);
      selBox.focus();
      selBox.select();
      document.execCommand('copy');
      document.body.removeChild(selBox);
    } catch {
      this.abcCore.openWindow(url);
    }
  }

  async openTargetUrl(): Promise<void> {
    this.closeExtentMenu();
    const name = this.mapLoader?.definition?.target?.name;
    const url = this.mapLoader?.definition?.target?.url;
    this.mapView.openUrl(this.abcCore, await this.mapView.replaceTargetUrlValues(url), name);
  }

  viewBack(): void {
    if (this.mapView?.tryViewPrevious()) {
      this.click = true;
      setTimeout(() => {
        this.click = false;
        this.refreshViewData();
      }, this.delay);
    }
  }

  viewNext(): void {
    if (this.mapView?.tryViewNext()) {
      this.click = true;
      setTimeout(() => {
        this.click = false;
        this.refreshViewData();
      }, this.delay);
    }
  }

  private onMapMoveEnd(event: any, parent: any) {
    if (parent.click === true || parent.olmap != this.olmap) return;

    this.mapView?.updateHistory();
    this.refreshViewData();
  }

  private refreshViewData() {
    this.refreshNaviButtonState();
  }

  private refreshNaviButtonState() {
    this.enablePrevButton = this.mapView.canViewPrevious();
    this.enableNextButton = this.mapView.canViewNext();
    this.changeDetectorRef.detectChanges();
  }

  centerGeoLocation() {
    const geo = this.geoLocationPointFeature?.getGeometry();
    if (geo) {
      this.mapView.setCenter(geo.getCoordinates());
    }
  }

  private drawGeoLocationPoint(coordinate: GeoLocationPosition, color: string): boolean {
    if (coordinate.latitude || coordinate.longitude) {
      this.ensureGeoLocationPointFeature();

      this.geoLocationPointFeature.setStyle(
        new ol.style.Style({
          image: new ol.style.Circle({
            radius: 6,
            fill: new ol.style.Fill({
              color: color,
            }),
            stroke: new ol.style.Stroke({
              color: '#fff',
              width: 2,
            }),
          }),
        }),
      );

      let coordinates = [coordinate.longitude, coordinate.latitude, coordinate.altitude];
      let proj = this.olmap.getView().getProjection();
      coordinates = ol.proj.fromLonLat(coordinates, proj);

      this.geoLocationPointFeature.setGeometry(new ol.geom.Point(coordinates));
      return true;
    }
    return false;
  }

  private ensureGeoLocationPointFeature() {
    if (!this.geoLocationPointFeature) {
      {
        this.geoLocationPointFeature = new ol.Feature();
        this.geoLocationPointLayer = new ol.layer.Vector({
          map: this.olmap,
          source: new ol.source.Vector({
            features: [this.geoLocationPointFeature],
          }),
        });
      }
    }
  }

  private removeGeoLocationPointFeature() {
    if (this.geoLocationPointFeature) {
      {
        this.geoLocationPointFeature = null;
        if (this.geoLocationPointLayer != null) {
          this.geoLocationPointLayer.setMap(null);
          this.geoLocationPointLayer = null;
        }
      }
    }
  }

  private async initMap(): Promise<void> {
    if (this.currentMapConfiguration) {
      this.mapLoader = new MapLoader(
        this.abcCore,
        this.coreDataService,
        this.configService,
        this.getWebApiUrl(this.currentMapConfiguration.WebApiUrl, this.ReadMapUrl),
        this.getWebApiUrl(this.currentMapConfiguration.WebApiUrl, this.ReadMapImageUrl),
        this.getWebApiUrl(this.currentMapConfiguration.WebApiUrl, this.TransformMapValueUrl),
        this.mapid,
        this.overviewid,
        this.attributionid,
        this.mapscaleid,
        this.rotateid,
        this.mapService,
        this.parameterService,
        this.res,
      );
      this.mapLoader
        .loadMap(
          (DotNetBridge.hasContext ? await DotNetBridge.context.get('ReadMapMessage') : null) ??
            this.currentMapConfiguration.ReadMapMessage,
          this.currentMapConfiguration.DefaultExtent,
          this.currentMapConfiguration.InitialExtent,
          this.currentMapConfiguration.ImageVectorScaleLimit,
          this.currentMapConfiguration.MinScaleLimit,
          this.currentMapConfiguration.CacheFilterExpression,
        )
        .subscribe(
          (olmap) => {
            this.olmap = olmap;
            this.mapView = this.mapLoader.createMapView();
            this.mapView?.setRotationEnabled(this.mapRotationEnabled);

            this.mapSelection = new MapSelection(
              this.olmap,
              this.mapLoader,
              this.mapView,
              this.mapService,
              this.objectSpaceService,
            );

            this.mapService.raiseMapLoaded(this.mapSelection);

            this.mapService.onContextMenuConfigReceived().subscribe((config) => this.initContextMenuController(config));
            this.mapService.queryShortestPath = (model) => this.mapLoader.queryShortestPath(model);

            this.mapScaletext.initMap(this.mapView, this.mapLoader.getScales());
            this.isMapLoaded = true;

            this.olmap.addEventListener('moveend', (e) => this.onMapMoveEnd(e, this));

            this.dragPan = new ol.interaction.DragPan({
              condition: function (e) {
                const noModifierKeys = ol.events?.condition?.noModifierKeys;
                if (noModifierKeys && !noModifierKeys(e)) {
                  return false;
                }
                return e.originalEvent?.button == 1;
              },
              onFocusOnly: true,
            });

            this.olmap.addInteraction(this.dragPan);

            if (this.mapLoader.hasTooltips) {
              this.initMapTooltips();
            } else {
              this.mapTooltip.hide();
            }

            this.profileService.getCurrentProfile(true).subscribe(
              (profileInfo) => this.notifyProfileInfo(profileInfo),
              (err) => this.abcCore.error.publish(err),
            );
            this.initMapCursor();
          },
          (error) => {
            this.abcCore.error.publish(error);
            this.mapService.raiseMapLoaded(null);
          },
        );
    }
  }

  private initMapTooltips() {
    this.mapTooltip.initMapOverlay(this.olmap);
    const source = interval(100);
    let lastPixel: number[];
    let currentPixel: number[];
    let currentCoordinate: number[];
    let lastTime: number = Date.now();
    let isRunning: boolean = false;

    const timeout: number = 3000;

    this.subscriptions.push(
      source.subscribe((_) => {
        try {
          let hide = this.contextMenuController?.isContextMenuVisible();
          if (isRunning) return;
          isRunning = true;
          const currentTime = Date.now();
          if (
            currentTime - lastTime >= 250 &&
            lastPixel &&
            currentPixel &&
            currentCoordinate &&
            ((Math.abs(lastPixel[0] - currentPixel[0]) <= 1 && Math.abs(lastPixel[1] - currentPixel[1]) <= 1) ||
              currentTime - lastTime >= timeout)
          ) {
            lastTime = currentTime + timeout;
            let shown = false;
            this.olmap.forEachFeatureAtPixel(
              currentPixel,
              (feature: any, layer: any) => {
                const tt = this.mapLoader.tryGetTooltips(feature, layer);
                if (tt?.values && !shown && !hide) {
                  shown = true;
                  this.mapTooltip.show(currentCoordinate, tt.layerName, tt.values);
                }
              },
              { hitTolerance: 10 },
            );
            if (!shown) {
              hide = true;
            }
          }
          if (hide) {
            this.mapTooltip.hide();
          }
        } catch (e) {
          console.error(e);
        } finally {
          isRunning = false;
        }
        lastPixel = currentPixel;
      }),
    );
    this.olmap.on('pointermove', (e) => {
      currentPixel = e.pixel;
      currentCoordinate = e.coordinate;
      lastTime = Date.now();
    });
  }

  private initMapCursor() {
    this.olmap.on('pointermove', (e) => {
      if (this.action == ActionType.Pan) {
        e.map.getTargetElement().style.cursor = 'pointer';
      } else {
        e.map.getTargetElement().style.cursor = '';
      }
    });
  }

  // private setCursorStyle(cursor: string) {
  //   const target = this.olmap.getTargetElement();
  //   if (target) {
  //     target.style.cursor = cursor || '';
  //   }
  // }

  private loadMeasureComponent() {
    if (!this.measureComponent) {
      const fcFactory = this.componentResolver.resolveComponentFactory(MeasureComponent);
      const viewContainerRef = this.fcHost.viewContainerRef;

      const measureComponentRef = viewContainerRef.createComponent(fcFactory);
      this.changeDetectorRef.detectChanges();

      this.measureComponent = <MeasureComponent>measureComponentRef.instance;
    }
  }

  private initContextMenuController(config: ContextMenuConfiguration) {
    if (this.contextMenuController == null) {
      this.contextMenuController = new ContextMenuController(
        this.componentResolver,
        this.fcHost.viewContainerRef,
        config,
        this.changeDetectorRef,
        this.abcCore,
        this.mapService,
        this.olmap,
      );
    }

    this.mapService.executeCommand = async (name, entities) => {
      if (config?.definitions && entities?.length) {
        for (const def of config.definitions) {
          if (def?.buttons) {
            for (const b of def.buttons) {
              if (b.name === name && b.action) {
                await b.action(entities[0]);
              }
            }
          }
        }
      }
    };
    if (this.initialCommandInfo) {
      this.mapService.executeCommand(this.initialCommandInfo.name, this.initialCommandInfo.entities);
      this.initialCommandInfo = null;
    }

    this.updateContextMenuState();
    this.checkAvailabilityNewTicketAction(config);
  }

  private checkAvailabilityNewTicketAction(config: ContextMenuConfiguration) {
    let availableNewTicketAction = false;
    if (config) {
      for (let c of config.definitions) {
        const newTicketButton = c.buttons.find((b) => b.name === this.NEW_TICKET_ACTION);
        if (newTicketButton) {
          availableNewTicketAction = true;
          break;
        }
      }
    }

    this.hasActionNewTicket = availableNewTicketAction;
  }

  private getWebApiUrl(baseurl: string, action: string) {
    let url = baseurl;
    if (!url.endsWith('/') && !action.startsWith('/')) url += '/';
    url += action;
    return url;
  }

  updateDrawInteraction() {
    if (this.action == ActionType.Default) {
      this.clearEditInteractions();
    } else {
      this.clearEditInteractions();
      this.ensureDrawInteractions();
    }
  }

  ensureDrawInteractions() {
    this.ensureEditSource();
    let drawType: string = undefined;
    switch (this.action) {
      case ActionType.InsertPoint:
        drawType = 'Point';
        break;
      case ActionType.InsertLine:
        drawType = 'LineString';
        break;
      case ActionType.InsertPolygon:
        drawType = 'Polygon';
        break;
      case ActionType.InsertTicket:
        drawType = 'Point';
        break;
    }

    if (drawType) {
      this.draw = new ol.interaction.Draw({
        source: this.vectorSource,
        type: drawType,
      });

      this.draw.on('drawend', (event) => this.onInsertGeometry(event, drawType));
      this.olmap.addInteraction(this.draw);
      this.snap = new ol.interaction.Snap({ source: this.vectorSource });
      this.olmap.addInteraction(this.snap);
    }
  }
  onInsertGeometry(event: any, drawType: string) {
    if (event) {
      let feature = event.feature;
      if (feature) {
        let geom = feature.getGeometry();
        if (geom) {
          let result = this.findFeatureWithZCoord(geom);

          if (this.action === ActionType.InsertTicket) {
            this.mapService.raiseMapAction({
              actionId: MapActionIds.CREATE_TICKET,
              feature: result?.feature ?? feature,
              isFirstConstructionAction: this.isFirstConstructionAction,
            });

            setTimeout(() => {
              this.toolAddTicket.checked = false;
              this.action = ActionType.Default;
              this.clearEditInteractions();
              this.updateContextMenuState();
            }, 200);
          } else {
            this.mapService.raiseMapAction({
              actionId: MapActionIds.CREATE_OBJECT,
              feature: result?.feature ?? feature,
              isFirstConstructionAction: this.isFirstConstructionAction,
            });
          }

          this.isFirstConstructionAction = false;
        }
      }
    }
  }

  private findFeatureWithZCoord(geom: any): { feature: any; z_coord: number } {
    let z_coord: number = undefined;
    let feature: any = undefined;
    if (geom.getLastCoordinate) {
      const coord = geom.getLastCoordinate();
      if (coord) {
        let distance: number = undefined;
        const pixel = this.olmap.getPixelFromCoordinate(coord);
        this.olmap.forEachFeatureAtPixel(
          pixel,
          (f: any, layer: any) => {
            if (layer?.layerdefinition?.selectable !== false) {
              if (
                layer?.layerdefinition?.type === MapLayerType[MapLayerType.Dbms] ||
                layer?.layerdefinition?.type === MapLayerType[MapLayerType.Shp] ||
                layer?.layerdefinition?.type === MapLayerType[MapLayerType.ObjectSpace]
              ) {
                const c = f.getGeometry().getFirstCoordinate();
                if (c && f?.get('z_coord')) {
                  let dist = Math.sqrt((c[0] - coord[0]) * (c[0] - coord[0]) + (c[1] - coord[1]) * (c[1] - coord[1]));
                  if (distance === undefined || dist < distance) {
                    z_coord = f?.get('z_coord');
                    distance = dist;
                    feature = f;
                  }
                }
              }
            }
          },
          { hitTolerance: 10 },
        );
      }
    }
    if (z_coord !== undefined) {
      this.mapService.sendMapShortMessage(z_coord.toString());

      return { feature: feature, z_coord: this.mapService.getZValue(z_coord) };
    }
  }

  ensureEditSource() {
    this.vectorSource = new ol.source.Vector();
    this.vectorEditlayer = this.createVectorLayer(this.vectorSource);
  }

  private ensureEditSourceForMarker() {
    this.vectorSource = new ol.source.Vector();
    this.vectorEditlayer = this.createMarkerLayer(this.vectorSource);
    if (this.mapView && this.mapView.olmap) {
      this.mapView.olmap.addLayer(this.vectorEditlayer);
    }
  }

  private createMarkerLayer(src: any) {
    const svgUrl = this.res.svgUrl('Map-LocationMarker-Image');
    const icon = new ol.style.Icon({
      src: svgUrl,
      anchor: [0.5, 1],
      scale: 1.2,
      crossOrigin: 'anonymous',
    });

    return new ol.layer.Vector({
      source: src,
      style: new ol.style.Style({
        fill: new ol.style.Fill({
          color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new ol.style.Stroke({
          color: '#ffcc33',
          width: 2,
        }),
        image: icon,
        // image: new ol.style.Circle({
        //   radius: 7,
        //   stroke: new ol.style.Stroke({
        //     color: 'black',
        //     width: 1,
        //   }),
        //   fill: new ol.style.Fill({
        //     color: '#ffcc33',
        //   }),
        // }),
      }),
      zIndex: Infinity,
    });
  }

  createVectorLayer(src: any): any {
    let vector = new ol.layer.Vector({
      source: src,
      style: new ol.style.Style({
        fill: new ol.style.Fill({
          color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new ol.style.Stroke({
          color: '#ffcc33',
          width: 2,
        }),
        image: new ol.style.Circle({
          radius: 7,
          stroke: new ol.style.Stroke({
            color: 'black',
            width: 1,
          }),
          fill: new ol.style.Fill({
            color: '#ffcc33',
          }),
        }),
      }),
      zIndex: Infinity,
    });

    return vector;
  }

  clearEditInteractions() {
    if (this.olmap) {
      this.olmap.removeInteraction(this.draw);
      this.olmap.removeInteraction(this.snap);
      this.olmap.removeLayer(this.vectorEditlayer);
      this.vectorEditlayer = undefined;
      this.vectorSource = undefined;
    }
  }

  onMeasureValChange(newaction: ActionType) {
    const oldaction = this.action;
    this.customizeInteractions(false);

    if (newaction == ActionType.MeasureLine && this.toolMeasureLine.checked) {
      this.action = ActionType.MeasureLine;
    } else if (newaction == ActionType.MeasureArea && this.toolMeasureArea.checked) {
      this.action = ActionType.MeasureArea;
    } else if (newaction == ActionType.MeasureCoord && this.toolMeasureCoord.checked) {
      this.action = ActionType.MeasureCoord;
    } else if (newaction == ActionType.ZoomWin && this.toolZoomArea.checked) {
      this.action = ActionType.ZoomWin;
    } else if (newaction == ActionType.InsertLine && this.toolAddLine.checked) {
      this.action = ActionType.InsertLine;
    } else if (newaction == ActionType.InsertPoint && this.toolAddPoint.checked) {
      this.action = ActionType.InsertPoint;
    } else if (newaction == ActionType.InsertTicket && this.toolAddTicket.checked) {
      this.action = ActionType.InsertTicket;
    } else if (newaction == ActionType.InsertPolygon && this.toolAddPolygon.checked) {
      this.action = ActionType.InsertPolygon;
    } else if (newaction == ActionType.Pan && this.toolPan.checked) {
      this.action = ActionType.Pan;
    } else if (newaction == ActionType.SelectEntities && this.toolSelectEntities.checked) {
      this.action = ActionType.SelectEntities;
    } else {
      this.action = ActionType.Default;
    }

    this.isFirstConstructionAction =
      this.action === ActionType.InsertPoint ||
      this.action === this.actionType.InsertLine ||
      this.action === this.actionType.InsertPolygon ||
      this.action === this.actionType.InsertTicket;

    switch (this.action) {
      case ActionType.MeasureLine:
        if (this.toolMeasureLine) {
          this.closeMeasureMenu();
          this.toolMeasureLine.checked = true;
        }
        break;

      case ActionType.MeasureArea:
        if (this.toolMeasureArea) {
          this.closeMeasureMenu();
          this.toolMeasureArea.checked = true;
        }
        break;

      case ActionType.MeasureCoord:
        if (this.toolMeasureCoord) {
          this.closeMeasureMenu();
          this.toolMeasureCoord.checked = true;
        }
        break;

      case ActionType.ZoomWin:
        if (this.toolZoomArea) {
          this.toolZoomArea.checked = true;
        }
        break;

      case ActionType.InsertLine:
        if (this.toolAddLine) {
          this.closeConstructionMenu();
          this.toolAddLine.checked = true;
        }
        break;

      case ActionType.InsertPoint:
        if (this.toolAddPoint) {
          this.closeConstructionMenu();
          this.toolAddPoint.checked = true;
        }
        break;

      case ActionType.InsertTicket:
        if (this.toolAddTicket) {
          this.closeConstructionMenu();
          this.toolAddTicket.checked = true;
        }
        break;

      case ActionType.InsertPolygon:
        if (this.toolAddPolygon) {
          this.closeConstructionMenu();
          this.toolAddPolygon.checked = true;
        }
        break;

      case ActionType.Pan:
        if (this.toolPan) {
          this.toolPan.checked = true;
          this.customizeInteractions(true);
        }
        break;

      default:
        break;
    }

    if (this.action !== ActionType.Pan) {
      this.toolPan.checked = false;
    }

    if (this.action !== ActionType.ZoomWin) {
      this.toolZoomArea.checked = false;
    }

    if (oldaction != this.action) {
      this.updateMeasureInteraction();
      this.updateContextMenuState();
      this.updateDrawInteraction();

      if (this.action === ActionType.ZoomWin) {
        this.mapView?.beginDragZoom();
      } else if (oldaction == ActionType.ZoomWin) {
        this.mapView?.endDragZoom();
      }

      if (this.action === ActionType.SelectEntities) {
        this.toolSelectEntities.checked = true;
        const deactivatedInteractions: any[] = [];
        this.olmap?.interactions.forEach((interaction) => {
          const isDragPan = interaction instanceof ol.interaction.DragPan;
          if (isDragPan && interaction !== this.dragPan && interaction.getActive()) {
            interaction.setActive(false);
            deactivatedInteractions.push(interaction);
          }
        });
        const reactivate: () => void = () => {
          deactivatedInteractions.forEach((_) => _.setActive(true));
          this.updateContextMenuState();
        };

        this.draw = new ol.interaction.Draw({
          source: this.vectorSource,
          type: 'Polygon',
        });
        this.olmap.addInteraction(this.draw);

        this.draw.on('drawabort', (event) => reactivate());

        this.draw.on('drawend', (event) => {
          let selectedEntities: DataEntity[] = [];
          const geojson = new ol.format.GeoJSON();
          const polygonData = geojson.writeFeatureObject(event.feature);
          const polygon = event.feature.getGeometry();
          const polygonExtent = polygon.getExtent();
          const allVLayers = this.mapLoader.getVectorLayers();
          for (const l of allVLayers) {
            if (l?.layerdefinition?.selectable !== false && l.getVisible()) {
              const src = l.getSource();
              src.forEachFeatureIntersectingExtent(polygonExtent, (feature) => {
                const featureData = geojson.writeFeatureObject(feature);
                if (!turf.booleanDisjoint(polygonData, featureData)) {
                  const entity = MapLoader.toDataEntity(feature);
                  if (entity) {
                    selectedEntities.push(entity);
                  }
                }
              });
            }
            reactivate();
          }

          const uniqueObjectsMap = new Map<string, DataEntity>();
          selectedEntities.forEach((e) => {
            uniqueObjectsMap.set(e.id, e);
          });

          selectedEntities = Array.from(uniqueObjectsMap.values());
          if (selectedEntities.length === 0) {
            this.mapService.sendMapShortMessage(this.res.get('No-Object-Selected'));
          } else {
            if (this.selectionEntitiesAction) {
              this.selectionEntitiesAction(selectedEntities);
            } else if (this.selectEntitiesDefaultAction) {
              this.selectEntitiesDefaultAction(selectedEntities);
            }
            setTimeout(() => {
              this.onMeasureValChange(ActionType.Default);
            }, 100);
          }
        });
      } else if (oldaction == ActionType.SelectEntities) {
        this.toolSelectEntities.checked = false;
      }
    }
  }

  private customizeInteractions(isPanMode: boolean) {
    if (isPanMode)
      this.olmap?.interactions.forEach((interaction) => {
        if (!(interaction instanceof ol.interaction.DragPan) && !(interaction instanceof ol.interaction.MouseWheelZoom))
          interaction.setActive(false);
      });
    else {
      this.olmap?.interactions.forEach((interaction) => {
        interaction.setActive(true);
      });
    }
  }

  updateMeasureInteraction() {
    if (
      this.action !== ActionType.MeasureArea &&
      this.action !== ActionType.MeasureLine &&
      this.action !== ActionType.MeasureCoord &&
      this.action !== ActionType.ZoomWin &&
      this.action !== ActionType.Pan &&
      this.action !== ActionType.SelectEntities
    ) {
      if (this.measureComponent) {
        var removeMeasureElements: boolean = !(this.action == ActionType.PrintMap);
        this.measureComponent.clearInteraction(removeMeasureElements);
      }

      this.clearEditInteractions();
    } else {
      if (!this.measureComponent) this.loadMeasureComponent();

      this.measureComponent?.ensureInteraction(this.mapView, this.action);
    }
  }

  updateContextMenuState() {
    this.contextMenuController?.setActive(this.action === ActionType.Default);
  }

  startNetworkTracing(url: string) {
    if (!this.networkTracingDialog) {
      const style: WindowStyle = {
        height: '430',
        width: '400',
        minWidth: '150',
        resizable: false,
        position: { my: 'top left', at: 'top left', offset: '60 10' },
      };
      const WindowAttr: IWindow = {
        id: 'networktracingDialog',
        label: this.res.get('Map-NetworkTracing'),
        maximizeButton: false,
        minimizeButton: false,
        component: NetworktracingComponent,
        setWindowStyle: (windowStyle) => (windowStyle = style),
      };
      const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
      this.windowInstance = <AbcWindowComponent>window.componentRef.instance;

      this.windowInstance.createComponentInstance().subscribe((activeComponent) => {
        const w = activeComponent.openWindow();
        const traceInstance = w.instance;
        this.onNetworkTracingDialogOpened(traceInstance, url);
        this.changeDetectorRef.detectChanges();

        this.openWindowInstances.push(this.windowInstance);

        this.windowInstance.onAfterClosed.subscribe(() => {
          this.closeNetworkTraceingDialog.bind(this)();
          CollectionHelper.removeItem(this.openWindowInstances, this.windowInstance);
        });
      });
    }
  }

  onNetworkTracingDialogOpened(traceInstance: any, url: string) {
    this.networkTracingDialog = traceInstance;
    this.networkTracingDialog.closeNetworkTracingDialog.subscribe(() => this.closeNetworkTraceingDialog());
    this.networkTracingDialog.init(url);
  }

  closeNetworkTraceingDialog() {
    if (this.networkTracingDialog) {
      this.networkTracingDialog.setVisible(false);
      this.networkTracingDialog = undefined;
    }

    this.windowInstance?.closeDialog();
    this.changeDetectorRef.detectChanges();
    this.windowInstance = undefined;
  }

  openPrintDialog() {
    this.action = ActionType.PrintMap;
    this.clearEnvForPrinting();
    this.mapView?.endDragZoom();

    const style: WindowStyle = {
      height: 'auto',
      width: '100%',
      maxWidth: '600',
      minWidth: '150',
      resizable: false,
      position: { my: 'top left', at: 'top left', offset: '0 60' },
    };
    const windowAttr: IWindow = {
      id: this.PRINT_DIALOG_ID,
      label: this.res.get('Map-PrintSettings'),
      maximizeButton: false,
      minimizeButton: false,
      component: PrintComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
      storeState: true,
      storeStateKey: this.PRINT_DIALOG_ID,
    };
    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;

    windowInstance.createComponentInstance().subscribe((windowComponent: any) => {
      const printInstance: PrintComponent = <PrintComponent>windowComponent.openWindow().instance;
      const element = document.querySelector('.cdk-overlay-container') as HTMLElement;
      const zindex = element?.style?.getPropertyValue('z-index');
      const indexvalue = element?.style?.getPropertyValue('--index-value');

      this.openWindowInstances.push(windowInstance);

      printInstance.printDialogCloseDemand.subscribe(() => {
        this.action = ActionType.Default;
        this.updateMeasureInteraction();
        this.updateContextMenuState();
        windowInstance.closeDialog();
        printInstance.clearPrintEnvironment();
        this.changeDetectorRef.detectChanges();
      });

      windowInstance.onClosing
        .asObservable()
        .pipe(
          concatMap((event: any) => {
            event.cancel = !printInstance.hideProgress;
            return of(null);
          }),
        )
        .subscribe(() => {});

      windowInstance.onAfterOpened.subscribe(async () => {
        await printInstance.initMap(this.mapView, this.mapLoader, this.profileInfo.id);
        printInstance.setVisible(true);
        if (element?.style) {
          element.setAttribute('style', 'z-index: 2501 !important');
          element.style.setProperty('--index-value', '2501');
        }
      });

      windowInstance.onAfterClosed.subscribe(() => {
        printInstance.cancel();
        if (this.featureSelector) {
          this.featureSelector.close();
        }
        this.mapSelection?.clearSelectedFeatures();
        this.mapView?.endDragZoom();
        this.action = ActionType.Default;
        this.updateContextMenuState();
        if (element?.style) {
          element.setAttribute('style', `z-index: ${zindex}`);
          element.style.setProperty('--index-value', indexvalue);
        }
        CollectionHelper.removeItem(this.openWindowInstances, windowInstance);
      });

      windowInstance.onMaximizeClick.subscribe(() => {
        printInstance.setPrintFeatureLayerVisibility(true);
      });

      windowComponent.onAfterClosed.subscribe(() => window.componentRef.destroy());
    });
  }

  clearEnvForPrinting() {
    if (this.toolMeasureLine) {
      this.toolMeasureLine.checked = false;
    }

    if (this.toolMeasureArea) {
      this.toolMeasureArea.checked = false;
    }

    if (this.toolZoomArea) {
      this.toolZoomArea.checked = false;
    }

    if (this.toolMeasureCoord) {
      this.toolMeasureCoord.checked = false;
    }

    if (this.toolPan) {
      this.toolPan.checked = false;
    }

    if (this.toolSelectEntities) {
      this.toolSelectEntities.checked = false;
    }

    this.changeDetectorRef.detectChanges();

    this.updateMeasureInteraction();
    this.updateContextMenuState();
  }

  public isMeasureAction(actionToCheck: ActionType = null) {
    return (
      this.action != null &&
      (this.action == actionToCheck ||
        (actionToCheck == null &&
          (this.action == ActionType.MeasureCoord ||
            this.action == ActionType.MeasureLine ||
            this.action == ActionType.MeasureArea)))
    );
  }

  public isConstructionAction(actionToCheck: ActionType = null) {
    return (
      this.action != null &&
      (this.action == actionToCheck ||
        (actionToCheck == null &&
          (this.action == ActionType.InsertPoint ||
            this.action == ActionType.InsertLine ||
            this.action == ActionType.InsertPolygon ||
            this.action == ActionType.InsertTicket)))
    );
  }

  public get actionType(): typeof ActionType {
    return ActionType;
  }

  public isMapPrintAvailable() {
    if (this.isMapPrintCommandAvailable === undefined && this.profileInfo?.settings) {
      const settings = new ProfileSettingsViewModel(this.profileInfo?.settings);
      this.isMapPrintCommandAvailable = settings.hasVisibleCommand('Map_Print');
    }
    return this.isMapPrintCommandAvailable;
  }

  private moveToNewCoordinate(coordinate: number[]): Promise<number[]> {
    return this.loadMovenodeController().then((moveNodeCtrl) => {
      this.moveNodeController = moveNodeCtrl;
      this.moveNodeController.cancelMoveNodeAction.subscribe(() => {
        this.cancelDrawing();
      });
      return new Promise((resolve, reject) => {
        let newCoordinate: number[];
        if (coordinate) {
          const controllerActive = this.contextMenuController?.active;
          this.contextMenuController?.setActive(false);

          this.showMoveNodeController(true);

          try {
            this.clearEditInteractions();
            this.ensureEditSource();
            this.draw = new ol.interaction.Draw({
              source: this.vectorSource,
              type: 'LineString',
              stopClick: true,
              minPoints: 2,
              maxPoints: 2,
            });

            this.olmap.addInteraction(this.draw);
            this.draw.appendCoordinates([coordinate]);

            this.draw.on('drawabort', (event) => {
              this.contextMenuController?.setActive(controllerActive);
              this.showMoveNodeController(false);
              resolve(undefined);
            });

            this.draw.on('drawend', (event) => {
              try {
                let origgeom = event.feature.getGeometry();
                let geom = this.mapView.transformGeometryToDataProjection(origgeom);
                newCoordinate = geom.getLastCoordinate();
                if (this.olmap) {
                  this.clearEditInteractions();
                }
                if (newCoordinate?.length > 1) {
                  let result = this.findFeatureWithZCoord(origgeom);
                  if (!CompareHelper.isNullorEmpty(result?.z_coord)) {
                    newCoordinate = [newCoordinate[0], newCoordinate[1], result.z_coord];
                  }
                }
                resolve(newCoordinate);
              } catch (err) {
                reject(err);
              } finally {
                setTimeout(() => {
                  this.contextMenuController?.setActive(controllerActive);
                  this.showMoveNodeController(false);
                }, 500);
              }
            });
          } catch (err) {
            this.contextMenuController?.setActive(controllerActive);
            this.showMoveNodeController(false);
            reject(err);
          }
        } else {
          resolve(undefined);
        }
      });
    });
  }

  private loadMovenodeController(): Promise<MovenodeControllerComponent> {
    return new Promise((resolve, reject) => {
      try {
        const fcFactory = this.componentResolver.resolveComponentFactory(MovenodeControllerComponent);
        const viewContainerRef = this.fcHost.viewContainerRef;

        const movenodeControllerComponentRef = viewContainerRef.createComponent(fcFactory);
        this.changeDetectorRef.detectChanges();

        const instance = movenodeControllerComponentRef.instance as MovenodeControllerComponent;
        resolve(instance);
      } catch (error) {
        reject(error);
      }
    });
  }

  private showMoveNodeController(show: boolean) {
    this.moveNodeController?.setVisible(show);
  }

  private selectDataEntitiesInteractively(): Promise<DataEntity[]> {
    const controllerActive = this.contextMenuController?.active;
    return this.loadFeaureGeometrySelector().then((selector) => {
      this.featureSelector = selector;
      this.featureSelector.setFeatureSelectionMode(FeatureSelectionMode.Selecting);
      this.featureSelector.isGeometryValid = true; //allow closing at any time
      this.minimizeParentPrintWindow();

      return new Promise((resolve, reject) => {
        try {
          this.contextMenuController?.setActive(false);
          const selectEntities: DataEntity[] = [];

          const clickHandler = (e) => {
            this.olmap.forEachFeatureAtPixel(
              e.pixel,
              (f) => {
                this.mapSelection?.toggleSelectedFeature(f);
              },
              {
                hitTolerance: 10,
              },
            );
          };

          this.olmap.on('singleclick', clickHandler);
          this.featureSelector.cancelfeatureSelection.subscribe(() => {
            this.olmap.un('singleclick', clickHandler);
            this.contextMenuController?.setActive(controllerActive);
            this.restoreParentPrintWindow();
          });

          this.featureSelector.featureSelectionClosed.subscribe(() => {
            this.olmap.un('singleclick', clickHandler);
            this.contextMenuController?.setActive(controllerActive);
            this.restoreParentPrintWindow();
            for (let f of this.mapSelection?.getSelectedFeatures()?.getArray()) {
              const entity = MapLoader.toDataEntity(f);
              if (entity) {
                selectEntities.push(entity);
              }
            }
            resolve(selectEntities);
          });
        } catch (err) {
          this.contextMenuController?.setActive(controllerActive);
          this.showFeatureGeometrySelector(false);
          this.restoreParentPrintWindow();
          reject(err);
        }
      });
    });
  }

  private minimizeParentPrintWindow() {
    if (!!this.openWindowInstances && this.openWindowInstances.length > 0) {
      const printWindowInstance = this.openWindowInstances.find((w) => w.id === this.PRINT_DIALOG_ID);
      if (printWindowInstance) {
        printWindowInstance.minimizeDialog();
        printWindowInstance.setMaximizeButton(false);
      }
    }
  }

  private restoreParentPrintWindow() {
    if (!!this.openWindowInstances && this.openWindowInstances.length > 0) {
      const printWindowInstance = this.openWindowInstances.find((w) => w.id === this.PRINT_DIALOG_ID);
      if (printWindowInstance) {
        printWindowInstance.maximizeDialog();
        printWindowInstance.setMaximizeButton(false);
        printWindowInstance.setMinimizeButton(false);
      }
    }
  }

  private drawFeatureGeometry(featureGeometryType: FeatureGeometryType): Promise<number[][]> {
    const controllerActive = this.contextMenuController?.active;
    return this.loadFeaureGeometrySelector().then((selector) => {
      this.featureSelector = selector;
      this.featureSelector.setFeatureGeometryType(featureGeometryType);
      this.featureSelector.featureSelectionClosed.subscribe(() => {
        if (this.draw) {
          this.draw.finishDrawing();
        }
        this.contextMenuController?.setActive(controllerActive);
      });

      this.featureSelector.cancelfeatureSelection.subscribe(() => {
        this.mapService.setFeatureSelectionCanceled(true);
        if (this.draw) {
          this.clearEditInteractions();
        }
        this.contextMenuController?.setActive(controllerActive);
      });

      return new Promise((resolve, reject) => {
        const drawType = this.getDrawTypeForCapturingGeometry(featureGeometryType);
        this.showFeatureGeometrySelector(true);

        try {
          this.clearEditInteractions();
          this.ensureEditSource();
          this.contextMenuController?.setActive(false);

          this.draw = new ol.interaction.Draw({
            source: this.vectorSource,
            type: drawType,
            style: this.getFeatureDrawStyle(),
            geometryFunction: (coordinates, geometry) =>
              this.geometryDrawFunction(coordinates, geometry, featureGeometryType, this.featureSelector),
          });

          this.olmap.addInteraction(this.draw);

          this.draw.on('drawabort', (event) => {
            this.contextMenuController?.setActive(controllerActive);
            this.showFeatureGeometrySelector(false);
            resolve(undefined);
          });

          this.draw.on('drawend', (event) => {
            try {
              const feature = event.feature;
              let origgeom = event.feature.getGeometry();
              if (featureGeometryType == FeatureGeometryType.Polygon) {
                this.tryClosePolygon(origgeom);
              }

              let geom = this.mapView.transformGeometryToDataProjection(origgeom);
              const coordinates = this.getFeatureGeometryAsCoordinates(featureGeometryType, geom);

              if (!this.validateDrawnGeometry(featureGeometryType, coordinates)) {
                this.vectorSource.removeFeature(feature);
                const invalidMsg = this.getInvalidGeometryWarning(featureGeometryType);
                this.mapService.sendMapShortMessage(invalidMsg);
                this.olmap.removeInteraction(this.draw);
                this.olmap.addInteraction(this.draw);

                return;
              }

              if (this.olmap) {
                this.clearEditInteractions();
              }

              resolve(coordinates);
            } catch (err) {
              reject(err);
            } finally {
              setTimeout(() => {
                this.contextMenuController?.setActive(controllerActive);
                this.showFeatureGeometrySelector(false);
              }, 500);
            }
          });
        } catch (err) {
          this.contextMenuController?.setActive(controllerActive);
          this.showFeatureGeometrySelector(false);
          reject(err);
        }
      });
    });
  }

  private tryClosePolygon(geom: any) {
    const coords = geom.getCoordinates?.()[0];
    if (coords && coords.length >= 3) {
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push([...first]);
        geom.setCoordinates([coords]);
      }
    }
  }

  private getInvalidGeometryWarning(featureGeometryType: FeatureGeometryType): string {
    if (featureGeometryType === FeatureGeometryType.Point) {
      return this.res.get('Controls-FeatureSelector-InvalidPoint');
    } else if (featureGeometryType === FeatureGeometryType.Line) {
      return this.res.get('Controls-FeatureSelector-InvalidLine');
    } else {
      return this.res.get('Controls-FeatureSelector-InvalidPolygon');
    }
  }

  private geometryDrawFunction(
    coords: [][],
    geom: any,
    type: FeatureGeometryType,
    featureGeometrySelector: FeatureSelectorComponent,
  ): any {
    if (!geom) {
      if (type == FeatureGeometryType.Point) {
        geom = new ol.geom.Point([]);
      } else if (type == FeatureGeometryType.Line) {
        geom = new ol.geom.LineString([]);
      } else if (type == FeatureGeometryType.Polygon) {
        geom = new ol.geom.Polygon([]);
      }
    }

    if (coords) {
      geom.setCoordinates(coords);
    }

    if (coords) {
      const coordinates = this.getFeatureGeometryAsCoordinates(type, geom);
      if (type == FeatureGeometryType.Point) {
        featureGeometrySelector.isGeometryValid = coordinates.length >= 2;
      } else if (type == FeatureGeometryType.Line) {
        featureGeometrySelector.isGeometryValid = coordinates.length >= 3;
      } else if (type == FeatureGeometryType.Polygon) {
        featureGeometrySelector.isGeometryValid = coordinates.length >= 4;
      }

      this.changeDetectorRef.detectChanges();
    }

    return geom;
  }

  private validateDrawnGeometry(featureGeometryType: FeatureGeometryType, coordinates: number[][]): boolean {
    let retValid = false;
    if (coordinates && Array.isArray(coordinates)) {
      if (featureGeometryType === FeatureGeometryType.Polygon && coordinates.length >= 4) {
        retValid = true;
      } else if (featureGeometryType === FeatureGeometryType.Line && coordinates.length >= 2) {
        retValid = true;
      } else if (featureGeometryType === FeatureGeometryType.Point && coordinates.length >= 1) {
        retValid = true;
      }
    }

    return retValid;
  }

  private getFeatureGeometryAsCoordinates(geometryType: FeatureGeometryType, geom: any): number[][] {
    let ret: number[][] = [];
    const coordinates = geom?.getCoordinates?.();
    if (coordinates && Array.isArray(coordinates)) {
      if (geometryType === FeatureGeometryType.Polygon) {
        if (Array.isArray(coordinates[0])) {
          ret = coordinates[0];
        }
      } else if (geometryType === FeatureGeometryType.Line) {
        ret = coordinates;
      } else if (geometryType === FeatureGeometryType.Point) {
        ret = coordinates;
      }
    }

    return ret;
  }

  private getDrawTypeForCapturingGeometry(featureGeometryType: FeatureGeometryType): string {
    let drawType: string = '';
    switch (featureGeometryType) {
      case FeatureGeometryType.Polygon:
        drawType = 'Polygon';
        break;
      case FeatureGeometryType.Line:
        drawType = 'LineString';
        break;
      case FeatureGeometryType.Point:
        drawType = 'Point';
        break;
      default:
        drawType = 'Polygon';
        break;
    }

    return drawType;
  }

  private loadFeaureGeometrySelector(): Promise<FeatureSelectorComponent> {
    return new Promise((resolve, reject) => {
      try {
        const fcFactory = this.componentResolver.resolveComponentFactory(FeatureSelectorComponent);
        const viewContainerRef = this.fcHost.viewContainerRef;

        const featureSelectorComponentRef = viewContainerRef.createComponent(fcFactory);
        this.changeDetectorRef.detectChanges();

        const instance = featureSelectorComponentRef.instance as FeatureSelectorComponent;
        resolve(instance);
      } catch (error) {
        reject(error);
      }
    });
  }

  private showFeatureGeometrySelector(show: boolean) {
    this.featureSelector?.setVisible(show);
  }

  private getFeatureDrawStyle() {
    return new ol.style.Style({
      fill: new ol.style.Fill({
        color: 'rgba(255, 255, 255, 0.2)',
      }),
      stroke: new ol.style.Stroke({
        color: 'rgba(0, 0, 0, 0.5)',
        lineDash: [10, 10],
        width: 2,
      }),
      image: this.getCrossStyle(),
    });
  }

  private getCrossStyle() {
    return new ol.style.RegularShape({
      fill: new ol.style.Fill({
        color: 'red',
      }),
      points: 4,
      radius1: 15,
      radius2: 1,
    });
  }
}
